import { Stack, OutlinedInput, Box, FormLabel, Typography } from '@mui/material';
import { Editor } from '../../components/wysiwyg/Editor';
import { DrawerHeader } from '../../drawer/DrawerHeader';
import { FormField } from '../../form/FormField';
import { graphql } from '../../gql';
import { useTypedForm } from '../../../../shared/fe/hooks/useForm';
import { useApiMutation, useApiQuery } from '../../query/hooks';
import { FC, useEffect, useState } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { mutationResponseHandler } from '../../utils/mutationResponseHandler';
import { z } from 'zod';
import { useTheme } from '@mui/material/styles';
import { useDebounceEffect } from '../../utils/useDebounceEffect';
import { DrawerWrapper } from '../../drawer/DrawerWrapper';
import { SelectJourneyMainMetric } from '../../SelectJourneyMainMetric/SelectJourneyMainMetric';
import { useSnackbar } from '../../store/snackbar';

const getJourneyQuery = graphql(`
	query journeyEditDrawer_getJourney($journeyId: UUID!) {
		getJourney(by: { id: $journeyId }) {
			name
			description
		}
	}
`);

const updateJourneyMutation = graphql(`
	mutation editJourney_update($journeyId: UUID!, $name: String!, $description: String) {
		updateJourney(by: { id: $journeyId }, data: { name: $name, description: $description }) {
			ok
			errorMessage
		}
	}
`);

export const journeyEditDrawerSchema = z.object({
	journeyId: z.string(),
});

const createFormSchema = (intl: IntlShape) =>
	z.object({
		name: z.string().min(1, intl.formatMessage({ id: 'form.edit-journey.error.empty-name' })),
	});

export const JourneyEditDrawer: FC<z.infer<typeof journeyEditDrawerSchema>> = ({ journeyId }) => {
	const [description, setDescription] = useState<string>('');
	const intl = useIntl();
	const theme = useTheme();
	const { openSnackbar } = useSnackbar();

	const form = useTypedForm({
		schema: createFormSchema(intl),
		mode: 'all',
		reValidateMode: 'onChange',
	});

	const { data, isPending } = useApiQuery(getJourneyQuery, {
		variables: { journeyId },
	});

	useEffect(() => {
		if (!isPending) {
			form.reset({
				name: data?.getJourney?.name ?? '',
			});
			setDescription(data?.getJourney?.description ?? '');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isPending]);

	const { mutate: updateJourney } = useApiMutation(updateJourneyMutation, {
		onSuccess: (result) =>
			mutationResponseHandler({
				result: result.updateJourney.ok,
				toastMessages: {
					error: intl.formatMessage({ id: 'drawer.edit-journey.update.error' }),
				},
				errorMessage: result.updateJourney.errorMessage,
				openSnackbar,
			}),
	});

	const { name } = form.watch();
	const { isValid, isDirty } = form.formState;

	useDebounceEffect(() => {
		if ((isDirty && isValid) || description !== data?.getJourney?.description) {
			updateJourney({ journeyId, name, description });
		}
	}, [description, name, isValid, journeyId, updateJourney, isPending, isDirty]);

	return (
		<DrawerWrapper>
			<DrawerHeader title={intl.formatMessage({ id: 'drawer.edit-journey.title' })} />
			<Box width={'100%'}>
				<form noValidate>
					<Stack gap={2}>
						<FormField
							form={form}
							name={'name'}
							label={intl.formatMessage({ id: 'drawer.edit-journey.label.name' })}
							render={(props) => <OutlinedInput {...props} />}
						/>
						<Box width={'100%'}>
							{!isPending && (
								<>
									<FormLabel>
										<Typography color={theme.palette.grey[600]}>
											<FormattedMessage id={'drawer.edit-journey.label.description'} />
										</Typography>
									</FormLabel>
									<Editor placeholder={'...'} state={data?.getJourney?.description ?? ''} setState={setDescription} />
								</>
							)}
						</Box>
						<FormLabel>
							<Typography color={theme.palette.grey[600]}>
								<FormattedMessage id={'drawer.edit-journey.label.mainMetric'} />
							</Typography>
						</FormLabel>
						<SelectJourneyMainMetric journeyId={journeyId} />
					</Stack>
				</form>
			</Box>
		</DrawerWrapper>
	);
};
